/** Sérialise une date-only (valeur d'un `<input type="date">`, « YYYY-MM-DD ») en
 * ISO UTC à midi. Midi UTC conserve le même jour calendaire à l'affichage local
 * (toLocaleDateString) sur toute la plage métropole + DOM-TOM (UTC-10 à UTC+11),
 * contrairement à T00:00:00Z (recule d'un jour en fuseau négatif) ou T23:59:59Z
 * (avance d'un jour en fuseau positif, ex. métropole UTC+1/+2). */
export function dueDateToIso(day: string): string {
  const [y, m, d] = day.split("-").map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d, 12)).toISOString();
}

/** Minuit local d'une clé « YYYY-MM-DD ». `new Date("YYYY-MM-DD")` lit minuit
 * UTC : la veille en fuseau négatif, et un pas en jours locaux qui glisse au
 * changement d'heure. */
export function parseDateKey(key: string): Date {
  const [y, m, d] = key.split("-").map(Number);
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1);
}

/** Valeur « YYYY-MM-DDTHH:mm » en heure locale (format de DateTimeField). */
export function toDateTimeLocal(d: Date): string {
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16);
}

/** Jour local « YYYY-MM-DD » (fuseau du navigateur). */
export function localDayKey(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Minuit local du jour de `d`, décalé de `days` jours (-1 : la veille). */
export function localMidnight(d: Date, days = 0): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + days);
}

// RFC3339 à la seconde, en UTC : la forme que TAWA lit sans ambiguïté.
function isoSeconds(d: Date): string {
  return d.toISOString().replace(/\.\d{3}Z$/, "Z");
}

/** Bornes d'un jour LOCAL « YYYY-MM-DD », en instants UTC : de minuit local à
 * la dernière seconde avant le minuit suivant. Sans fuseau, TAWA lit
 * `YYYY-MM-DDT00:00:00` comme de l'UTC : à Paris, les pointages de 00:00 à
 * 02:00 du jour tombaient dans la veille. */
export function localDayBounds(key: string): { from: string; to: string } {
  const start = parseDateKey(key);
  const next = localMidnight(start, 1);
  return {
    from: isoSeconds(start),
    to: isoSeconds(new Date(next.getTime() - 1000)),
  };
}

/** Dernier jour ouvré avant `d` (minuit local) : le vendredi un lundi, un
 * samedi ou un dimanche, la veille sinon. */
export function previousWorkday(d: Date): Date {
  const dow = d.getDay();
  const back = dow === 1 ? 3 : dow === 0 ? 2 : 1;
  return localMidnight(d, -back);
}
